import React, { useEffect, useRef, useState } from 'react';
import Chart from 'chart.js/auto';
import AdminSideMenu from '../../components/AdminSideMenu';
import api from "../../utils/axiosInstance";
import { Link } from 'react-router-dom';

const AdminDashboard = () => {
    const jobsChartRef = useRef(null);
    const applicationsChartRef = useRef(null);
    const jobsChartInstance = useRef(null);
    const applicationsChartInstance = useRef(null);
    const [isMenuOpen, setIsMenuOpen] = useState(false);
    const [assignmentsFilter, setAssignmentsFilter] = useState('all');
    const [revenueFilter, setRevenueFilter] = useState('all');
    const [chartLoading, setChartLoading] = useState(false);
    const [stats, setStats] = useState({
        totalCarriers: 0,
        totalDrivers: 0,
        totalInterests: 0
    });
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const fetchStats = async () => {
            try {
                const [carriersRes, driversRes, interestsRes] = await Promise.all([
                    api.get('/carriers/count'),
                    api.get('/drivers/count'),
                    api.get('/drivers/assigned-count')
                ]);

                setStats({
                    totalCarriers: carriersRes.data.count,
                    totalDrivers: driversRes.data.count,
                    totalInterests: interestsRes.data.count
                });
                setLoading(false);
            } catch (error) {
                console.error('Failed to fetch dashboard stats:', error);
                setLoading(false);
            }
        };

        fetchStats();
        fetchChartData();

        return () => {
            jobsChartInstance.current?.destroy();
            applicationsChartInstance.current?.destroy();
        };
    }, []);

    // Fetch chart data when filters change
    useEffect(() => {
        if (assignmentsFilter) {
            fetchChartData();
        }
    }, [assignmentsFilter]);

    useEffect(() => {
        if (revenueFilter) {
            fetchChartData();
        }
    }, [revenueFilter]);

    // Generate last 12 months for dropdown
    const generateMonthOptions = () => {
        const months = [];
        const now = new Date();

        for (let i = 0; i < 12; i++) {
            const date = new Date(now.getFullYear(), now.getMonth() - i, 1);
            const year = date.getFullYear();
            const month = String(date.getMonth() + 1).padStart(2, '0');
            const label = date.toLocaleDateString('en-US', { year: 'numeric', month: 'long' });

            months.push({
                value: `${year}-${month}`,
                label: label
            });
        }

        return months;
    };

    const monthOptions = generateMonthOptions();

    const fetchChartData = async () => {
        try {
            setChartLoading(true);

            // Fetch assignments data
            const assignmentsRes = await api.get(`/carriers/stats/monthly-assignments?filter=${assignmentsFilter}`);
            const assignmentsData = assignmentsRes.data;

            // Fetch revenue data
            const revenueRes = await api.get('/subscribed/stats/monthly-revenue?filter=' + revenueFilter);
            const revenueData = revenueRes.data;

            // Process assignments data
            const assignmentLabels = assignmentsData.map(item => {
                const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
                return `${monthNames[item._id.month - 1]} ${item._id.year}`;
            });
            const assignmentValues = assignmentsData.map(item => item.count);

            // Process revenue data
            const revenueLabels = revenueData.map(item => {
                const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
                return `${monthNames[item._id.month - 1]} ${item._id.year}`;
            });
            const revenueValues = revenueData.map(item => item.revenue);

            // Update charts
            updateCharts(assignmentLabels, assignmentValues, revenueLabels, revenueValues);

            setChartLoading(false);
        } catch (error) {
            console.error('Failed to fetch chart data:', error);
            setChartLoading(false);
        }
    };

    const updateCharts = (assignmentLabels, assignmentValues, revenueLabels, revenueValues) => {
        // Destroy existing charts
        if (jobsChartInstance.current) jobsChartInstance.current.destroy();
        if (applicationsChartInstance.current) applicationsChartInstance.current.destroy();

        // Create assignments chart
        jobsChartInstance.current = new Chart(jobsChartRef.current, {
            type: 'bar',
            data: {
                labels: assignmentLabels.length > 0 ? assignmentLabels : ['No Data'],
                datasets: [{
                    label: 'Driver Assignments',
                    backgroundColor: '#3c9bf9',
                    data: assignmentValues.length > 0 ? assignmentValues : [0],
                    borderRadius: 6
                }]
            },
            options: {
                responsive: true,
                maintainAspectRatio: false,
                plugins: {
                    legend: { display: false },
                    tooltip: {
                        callbacks: {
                            label: function (context) {
                                return 'Assignments: ' + context.parsed.y;
                            }
                        }
                    }
                },
                scales: {
                    y: {
                        beginAtZero: true,
                        ticks: {
                            stepSize: 1
                        }
                    },
                    x: { grid: { display: false } }
                }
            }
        });

        // Create revenue chart
        applicationsChartInstance.current = new Chart(applicationsChartRef.current, {
            type: 'line',
            data: {
                labels: revenueLabels.length > 0 ? revenueLabels : ['No Data'],
                datasets: [{
                    label: 'Revenue ($)',
                    borderColor: '#3c9bf9',
                    backgroundColor: 'rgba(60, 155, 249, 0.1)',
                    data: revenueValues.length > 0 ? revenueValues : [0],
                    tension: 0.3,
                    fill: true
                }]
            },
            options: {
                responsive: true,
                maintainAspectRatio: false,
                plugins: {
                    legend: { display: false },
                    tooltip: {
                        callbacks: {
                            label: function (context) {
                                return 'Revenue: $' + context.parsed.y.toLocaleString();
                            }
                        }
                    }
                },
                scales: {
                    y: {
                        beginAtZero: true,
                        ticks: {
                            callback: function (value) {
                                return '$' + value.toLocaleString();
                            }
                        }
                    },
                    x: { grid: { display: false } }
                }
            }
        });
    };

    return (
        <div className="flex flex-col min-h-screen">
            <div className="flex flex-1 pt-16 md:pt-0">
                <AdminSideMenu isMenuOpen={isMenuOpen} setIsMenuOpen={setIsMenuOpen} />

                <div className="flex-1 md:ml-64 min-h-screen p-4 md:p-8">
                    <header className="bg-gray-50 z-10 pb-4 md:sticky md:top-0">
                        <h1 className="text-2xl md:text-3xl font-bold text-gray-900">Dashboard</h1>
                        <p className="text-gray-600">Welcome back, Admin!</p>
                    </header>

                    <section className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 md:gap-4 mb-4 md:mb-6 mt-2">
                        {[
                            {
                                title: 'Active Carriers',
                                iconClass: 'fas fa-briefcase',
                                value: stats.totalCarriers,
                                link: '/admin/carriers-listing'
                            },
                            {
                                title: 'Active Drivers',
                                iconClass: 'fas fa-user',
                                value: stats.totalDrivers,
                                link: '/admin/drivers-listing'
                            },
                            {
                                title: 'Drivers Assigned',
                                iconClass: 'fas fa-user-check',
                                value: stats.totalInterests
                            }
                        ].map((item, i) => {
                            const CardContent = (
                                <div
                                    className="bg-white p-3 md:p-4 rounded-xl shadow-sm border-l-4 border-blue-600 hover:-translate-y-1 hover:shadow-xl transition duration-300"
                                >
                                    <div className="flex justify-between items-start">
                                        <div>
                                            <p className="text-gray-500 text-xs md:text-sm font-medium">{item.title}</p>
                                            <h3 className="text-xl md:text-2xl font-bold mt-1 text-gray-900">
                                                {loading ? (
                                                    <div className="inline-block h-5 md:h-6 w-8 md:w-10 bg-gray-200 rounded animate-pulse"></div>
                                                ) : (
                                                    item.value?.toLocaleString() || '0'
                                                )}
                                            </h3>
                                        </div>
                                        <div className="p-2 rounded-lg bg-blue-50 text-blue-600">
                                            <i className={`${item.iconClass} text-lg md:text-xl`} />
                                        </div>
                                    </div>
                                </div>
                            );

                            return item.link ? (
                                <Link to={item.link} key={i}>
                                    {CardContent}
                                </Link>
                            ) : (
                                <div key={i}>{CardContent}</div>
                            );
                        })}
                    </section>

                    {/* Charts */}
                    <section className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-4 md:mb-6">
                        {/* Driver Assignments Chart */}
                        <div className="bg-white p-3 md:p-4 rounded-xl shadow-sm">
                            <div className="flex justify-between items-center mb-3">
                                <h2 className="text-sm md:text-base font-semibold text-gray-900">Monthly Drivers Assigned</h2>
                                <select
                                    value={assignmentsFilter}
                                    onChange={(e) => setAssignmentsFilter(e.target.value)}
                                    className="text-xs border border-gray-300 rounded px-1 md:px-2 py-1 focus:outline-none focus:ring-1 focus:ring-blue-400"
                                    disabled={chartLoading}
                                >
                                    <option value="all">All Time</option>
                                    <option value="1year">Last 1 Year</option>
                                    <option value="6months">Last 6 Months</option>
                                    <optgroup label="Specific Month">
                                        {monthOptions.map(option => (
                                            <option key={option.value} value={option.value}>
                                                {option.label}
                                            </option>
                                        ))}
                                    </optgroup>
                                </select>
                            </div>
                            <div className="h-60 md:h-64 relative">
                                {chartLoading && (
                                    <div className="absolute inset-0 flex items-center justify-center bg-white bg-opacity-75">
                                        <div className="text-sm text-gray-500">Loading...</div>
                                    </div>
                                )}
                                <canvas ref={jobsChartRef}></canvas>
                            </div>
                        </div>

                        {/* Revenue Chart */}
                        <div className="bg-white p-3 md:p-4 rounded-xl shadow-sm">
                            <div className="flex justify-between items-center mb-3">
                                <h2 className="text-sm md:text-base font-semibold text-gray-900">Package Revenue Trend</h2>
                                <select
                                    value={revenueFilter}
                                    onChange={(e) => setRevenueFilter(e.target.value)}
                                    className="text-xs border border-gray-300 rounded px-1 md:px-2 py-1 focus:outline-none focus:ring-1 focus:ring-blue-400"
                                    disabled={chartLoading}
                                >
                                    <option value="all">All Time</option>
                                    <option value="1year">Last 1 Year</option>
                                    <option value="6months">Last 6 Months</option>
                                    <optgroup label="Specific Month">
                                        {monthOptions.map(option => (
                                            <option key={option.value} value={option.value}>
                                                {option.label}
                                            </option>
                                        ))}
                                    </optgroup>
                                </select>
                            </div>
                            <div className="h-60 md:h-64 relative">
                                {chartLoading && (
                                    <div className="absolute inset-0 flex items-center justify-center bg-white bg-opacity-75">
                                        <div className="text-sm text-gray-500">Loading...</div>
                                    </div>
                                )}
                                <canvas ref={applicationsChartRef}></canvas>
                            </div>
                        </div>
                    </section>
                </div>
            </div>

            {/* Overlay for mobile */}
            {isMenuOpen && (
                <div
                    className="fixed inset-0 bg-black bg-opacity-50 z-40 md:hidden"
                    onClick={() => setIsMenuOpen(false)}
                ></div>
            )}
        </div>
    );
};

export default AdminDashboard;